// The "no community yet" create affordance (S2.2). A small form — name (required),
// description, visibility (public | private), and who-can-post (#1838:
// all_members | moderators_only) — that POSTs to
// /communities/facility/{facilityId} via useCreateFacilityCommunity. A
// re-entrancy ref guards double-submit; a failed create keeps the form open (the
// hook surfaces an honest notification — no phantom success). Creation is gated on
// `facility:community` (server-authoritative): when the operator lacks it the
// submit is disabled AND the handler refuses to fire, so a denied operator can
// never send a create. The `onCreated` callback lets the page refetch its reader
// (so a 409 — the community already exists — flips it to the manage view).
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { notifications } from "@/app/notifications";

import { useCreateFacilityCommunity } from "./use-facility-community";

type Visibility = "public" | "private";
// #1838 — who may post into the community. `all_members` (default) keeps the
// membership-only behaviour; `moderators_only` makes it announcement-only (owner /
// moderators / facility operators post; members read + comment). The server is
// authoritative — it re-enforces the gate and returns a real 403 (PostPermissionError).
type PostPermission = "all_members" | "moderators_only";

interface CommunityCreateFormProps {
  facilityId: string;
  /** Server-authoritative facility:community grant. False ⇒ the control is
   *  disabled and the handler refuses to fire (no create). */
  canCreate: boolean;
  /** Called after the mutation settles (success OR failure) so the page can
   *  refetch its reader — a 409 then flips the page to the manage view. */
  onSettled?: () => void;
}

export function CommunityCreateForm({
  facilityId,
  canCreate,
  onSettled,
}: CommunityCreateFormProps) {
  const create = useCreateFacilityCommunity(facilityId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("public");
  const [postPermission, setPostPermission] =
    useState<PostPermission>("all_members");
  // Re-entrancy guard: a double-click fires the mutation at most once even before
  // React re-renders the disabled button.
  const submittingRef = useRef(false);

  const handleSubmit = async () => {
    // Server-authoritative gate — a denied operator never sends a create.
    if (!canCreate) return;
    const trimmed = name.trim();
    // Required-name gate — whitespace-only never reaches the API.
    if (!trimmed) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await create.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
        visibility,
        post_permission: postPermission,
      });
      // Success: clear the form + an honest success notification.
      notifications.success("Community created.");
      setName("");
      setDescription("");
      setVisibility("public");
      setPostPermission("all_members");
    } catch (err) {
      // Failure (incl. a 409 — a community already exists for this facility — or a
      // 403 RBAC denial): an honest error notification, NEVER a phantom success. The form
      // stays open with its values so the operator can retry. onSettled (below)
      // refetches the page reader, so a 409 flips the page to the manage view.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the community.",
      );
    } finally {
      submittingRef.current = false;
      onSettled?.();
    }
  };

  const busy = create.isPending;

  return (
    <Stack spacing={2}>
      <TextField
        disabled={busy || !canCreate}
        id="community-name"
        label="Name"
        onChange={(event) => setName(event.target.value)}
        placeholder="e.g. Smash Center Crew"
        required
        size="small"
        slotProps={{ htmlInput: { maxLength: 255 } }}
        value={name}
      />

      <TextField
        disabled={busy || !canCreate}
        id="community-description"
        label="Description"
        minRows={3}
        multiline
        onChange={(event) => setDescription(event.target.value)}
        placeholder="What this community is about, who it's for…"
        size="small"
        value={description}
      />

      <FormControl
        disabled={busy || !canCreate}
        size="small"
        sx={{ maxWidth: 240 }}
      >
        <InputLabel id="community-visibility-label">Visibility</InputLabel>
        <Select
          id="community-visibility"
          label="Visibility"
          labelId="community-visibility-label"
          onChange={(event) => setVisibility(event.target.value as Visibility)}
          value={visibility}
        >
          <MenuItem value="public">Public</MenuItem>
          <MenuItem value="private">Private</MenuItem>
        </Select>
      </FormControl>

      <FormControl
        disabled={busy || !canCreate}
        size="small"
        sx={{ maxWidth: 320 }}
      >
        <InputLabel id="community-post-permission-label">
          Who can post
        </InputLabel>
        <Select
          id="community-post-permission"
          label="Who can post"
          labelId="community-post-permission-label"
          onChange={(event) =>
            setPostPermission(event.target.value as PostPermission)
          }
          value={postPermission}
        >
          <MenuItem value="all_members">All members</MenuItem>
          <MenuItem value="moderators_only">
            Only moderators &amp; operators
          </MenuItem>
        </Select>
      </FormControl>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { xs: "stretch", sm: "center" } }}
      >
        <Button
          disabled={busy || !canCreate || !name.trim()}
          onClick={() => void handleSubmit()}
          size="small"
          sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
          type="button"
          variant="contained"
        >
          {busy ? "Creating…" : "Create community"}
        </Button>
        {!canCreate ? (
          <Typography color="text.secondary" variant="body2">
            You don't have permission to create this facility's community.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}
